import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../shared/Button";
import {
  HiOutlineUser,
  HiOutlineBriefcase,
  HiOutlineAcademicCap,
  HiOutlineCheckCircle,
  HiOutlineSparkles,
} from "react-icons/hi2";

const OnboardingChecklist = ({ profileCompletion = 0 }) => {
  const navigate = useNavigate();

  // Estimate completion for each section based on overall percentage
  // This is a simplified approach - adjust thresholds as needed based on your actual completion logic
  const checklist = useMemo(() => {
    const percentage = profileCompletion || 0;
    
    return [
      {
        id: "personal",
        label: "Complete Personal Information",
        icon: HiOutlineUser,
        completed: percentage >= 25,
        onClick: () => navigate("/profile/edit?tab=basic-info"),
      },
      {
        id: "professional",
        label: "Add Professional Details",
        icon: HiOutlineBriefcase,
        completed: percentage >= 50,
        onClick: () => navigate("/profile/experience"),
      },
      {
        id: "education",
        label: "Add Education & Certifications",
        icon: HiOutlineAcademicCap,
        completed: percentage >= 75,
        onClick: () => navigate("/profile/education"),
      },
    ];
  }, [profileCompletion, navigate]);

  const completedCount = checklist.filter((item) => item.completed).length;
  const remainingCount = checklist.length - completedCount;

  return (
    <div className="bg-white rounded-xl border border-surface-border p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-primary mb-2">
          Complete Your Profile
        </h3>
        <p className="text-sm text-grey">
          Keep your profile details current. You&apos;re{" "}
          <span className="font-semibold text-secondary-dark">{profileCompletion}% done</span>.
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-medium text-grey">Progress</span>
          <span className="text-xs font-semibold text-primary">
            {completedCount}/{checklist.length}
          </span>
        </div>
        <div className="w-full h-2 bg-surface-disabled rounded-full overflow-hidden">
          <div
            className="h-full bg-secondary rounded-full transition-all duration-300"
            style={{ width: `${(completedCount / checklist.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-3 mb-6">
        {checklist.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={item.onClick}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-surface-border hover:bg-surface-hover transition-colors text-left cursor-pointer focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-secondary focus-visible:outline-none"
            >
              {/* Icon Container */}
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                  item.completed
                    ? "bg-success/20"
                    : "bg-surface-disabled"
                }`}
              >
                {item.completed ? (
                  <HiOutlineCheckCircle
                    className="w-5 h-5 text-success"
                    aria-hidden="true"
                  />
                ) : (
                  <Icon
                    className="w-5 h-5 text-grey"
                    aria-hidden="true"
                  />
                )}
              </div>

              {/* Content */}
              <div className="flex-grow">
                <p
                  className={`text-sm font-medium ${
                    item.completed
                      ? "text-mediumgrey line-through"
                      : "text-primary"
                  }`}
                >
                  {item.label}
                </p>
              </div>

              {/* Status Indicator */}
              {item.completed && (
                <span className="flex-shrink-0 text-xs font-semibold text-success uppercase tracking-wider">
                  Done
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* CTA Button */}
      <Button
        onClick={() => navigate("/profile")}
        variant="default"
        color="primary"
        size="md"
        className="w-full"
      >
        Complete Profile
      </Button>

      {/* Info Text */}
      <p className="text-xs text-mediumgrey text-center mt-4 flex items-center justify-center gap-1.5">
        {remainingCount === 0
          ? (<>
            <HiOutlineSparkles className="w-4 h-4 text-secondary-dark" aria-hidden="true" />
            <span>Profile complete! Your details are up to date.</span>
          </>)
          : `${remainingCount} step${remainingCount !== 1 ? "s" : ""} remaining`}
      </p>
    </div>
  );
};

export default OnboardingChecklist;
